import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useStoreState, useStoreActions } from '~/store';
import {
  extractSkinFromPath,
  findSolutionBySkin,
  buildSkinUrl,
  shouldExcludeFromSkinRedirect,
} from './solutionUrl';

interface SkinRouteSyncProps {
  children: React.ReactNode;
}

/**
 * Syncs URL skin to easy-peasy store and handles backward compatibility redirects.
 * Replaces SolutionContext - all state lives in the store, this just bridges URL ↔ store.
 */
export function SkinRouteSync({ children }: SkinRouteSyncProps) {
  const history = useHistory();
  const location = useLocation();
  const solutions = useStoreState((state) => state.auth.solutions);
  const user = useStoreState((state) => state.auth.user);
  const storeSolutionId = useStoreState((state) => state.auth.solutionId);
  const changeSolution = useStoreActions(
    (actions) => actions.auth.changeSolution
  );
  // @ts-expect-error loading exists in store but not in types
  const loading = useStoreState((state) => state.auth.loading);

  const { skin, path } = extractSkinFromPath(location.pathname);
  const solutionFromUrl =
    skin && solutions?.length
      ? (findSolutionBySkin(solutions, skin) ?? null)
      : null;

  // Get default solution (from store or first available)
  const defaultSolution = solutions?.length
    ? (solutions.find((s) => s.id === storeSolutionId) ?? solutions[0])
    : null;

  // Check if URL skin mismatches store - need to sync
  const needsSync =
    user &&
    skin &&
    solutionFromUrl &&
    solutionFromUrl.id !== storeSolutionId &&
    !loading;

  // Sync URL skin to store
  useEffect(() => {
    if (needsSync) {
      changeSolution(solutionFromUrl.id);
    }
  }, [needsSync, solutionFromUrl, changeSolution]);

  // Handle backward compatibility redirects
  useEffect(() => {
    if (!user) return;
    if (location.pathname === '/') return;
    if (shouldExcludeFromSkinRedirect(location.pathname)) return;

    // If no skin in URL but we have a default solution, redirect
    if (!skin && defaultSolution?.skin && path !== '/') {
      const newUrl = buildSkinUrl(defaultSolution.skin, path);
      history.replace({
        pathname: newUrl,
        search: location.search,
        hash: location.hash,
      });
    }
  }, [skin, path, defaultSolution, user, location, history]);

  // Block rendering while switching solutions to avoid flash
  if (needsSync) return null;

  return <>{children}</>;
}
